import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safely render inline text with **bold** runs as real React nodes — no raw-HTML injection is ever performed.
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function renderMarkdownish(md) {
  const lines = md.split("\n");
  return lines.map((line, i) => {
    if (line.startsWith("# ")) return <h1 key={i} className="font-display text-3xl font-bold mt-6 mb-4">{line.slice(2)}</h1>;
    if (line.startsWith("## ")) return <h2 key={i} className="font-display text-2xl font-bold mt-6 mb-3">{line.slice(3)}</h2>;
    if (line.startsWith("### ")) return <h3 key={i} className="font-display text-xl font-bold mt-4 mb-2">{line.slice(4)}</h3>;
    const numMatch = line.match(/^(\d+)\.\s(.*)$/);
    if (numMatch) {
      return (
        <p key={i} className="my-2">
          <span className="font-semibold text-copper mr-2">{numMatch[1]}.</span>
          {renderInline(numMatch[2])}
        </p>
      );
    }
    if (line.trim() === "") return <div key={i} className="h-3" />;
    return <p key={i} className="my-2 text-slate-700">{renderInline(line)}</p>;
  });
}

export default function Terms() {
  const { kind } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState("");

  useEffect(() => {
    axios.get(`${API}/terms/${kind}`)
      .then(({ data }) => { setDoc(data); setContent(data.content); })
      .catch((err) => console.error("Failed to load terms:", err));
    setEditing(false);
  }, [kind]);

  const save = async () => {
    try {
      await axios.put(`${API}/terms/${kind}`, { content });
      toast.success("Terms updated");
      setEditing(false);
      const { data } = await axios.get(`${API}/terms/${kind}`);
      setDoc(data); setContent(data.content);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-8">
      <div className="mb-6 flex gap-2">
        <Link to="/terms/guest" data-testid="tab-guest-terms" className={`rounded-full px-4 py-2 text-sm font-semibold ${kind === "guest" ? "bg-copper text-white" : "border border-slate-200 text-slate-700"}`}>Guest Terms</Link>
        <Link to="/terms/host" data-testid="tab-host-terms" className={`rounded-full px-4 py-2 text-sm font-semibold ${kind === "host" ? "bg-copper text-white" : "border border-slate-200 text-slate-700"}`}>Host Agreement</Link>
      </div>

      <div className="rounded-3xl bg-white p-8 card-shadow">
        {editing ? (
          <>
            <textarea
              data-testid="terms-editor"
              value={content} onChange={(e) => setContent(e.target.value)}
              rows={24}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-sm outline-none focus:border-copper focus:bg-white"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => { setEditing(false); setContent(doc.content); }} className="btn-outline text-sm">Cancel</button>
              <button onClick={save} data-testid="save-terms-btn" className="btn-primary text-sm">Save</button>
            </div>
          </>
        ) : (
          <>
            <div className="prose max-w-none">
              {doc && renderMarkdownish(doc.content)}
            </div>
            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-4 text-xs text-slate-500">
              <span>Last updated: {doc ? new Date(doc.updated_at).toLocaleString() : ""}</span>
              {user?.is_admin && (
                <button onClick={() => setEditing(true)} data-testid="edit-terms-btn" className="btn-outline !py-1.5 !px-4 text-xs">Edit (Admin)</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
